/**
 * Token-bound link surface for monograph demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export interface MonographSurfaceLinkProps {
  readonly tokens: MonographPanelTokens;
  readonly href: string;
  readonly title: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly eyebrow?: React.ReactNode;
  readonly color?: string;
  readonly external?: boolean;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly titleStyle?: React.CSSProperties;
  readonly descriptionStyle?: React.CSSProperties;
}

export function MonographSurfaceLink({
  tokens,
  href,
  title,
  description,
  eyebrow,
  color = tokens.accent,
  external = true,
  className,
  style,
  titleStyle,
  descriptionStyle,
}: MonographSurfaceLinkProps): React.JSX.Element {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={cn("csk-monograph-surface-link", className)}
      style={{
        background: colorWithAlpha(tokens.void, "70"),
        border: `1px solid ${tokens.border}`,
        borderRadius: 12,
        color: tokens.text,
        display: "block",
        padding: "10px 12px",
        textDecoration: "none",
        transition: "background 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
        ...style,
      }}
      onFocus={(event) => {
        event.currentTarget.style.borderColor = colorWithAlpha(color, "aa");
        event.currentTarget.style.boxShadow = `0 0 12px ${colorWithAlpha(color, "24")}`;
      }}
      onBlur={(event) => {
        event.currentTarget.style.borderColor = tokens.border;
        event.currentTarget.style.boxShadow = "none";
      }}
    >
      {eyebrow ? (
        <div
          style={{
            color,
            fontFamily: tokens.mono,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.06em",
            marginBottom: 2,
            ...titleStyle,
          }}
        >
          {eyebrow}
        </div>
      ) : null}
      <div
        style={{
          color: tokens.bright,
          fontSize: 13.5,
          fontWeight: 750,
          lineHeight: 1.35,
          ...titleStyle,
        }}
      >
        {title}{" "}
        {external ? (
          <span aria-hidden style={{ color, fontSize: 11 }}>
            ↗
          </span>
        ) : null}
      </div>
      {description ? (
        <div
          style={{
            color: tokens.muted,
            fontSize: 12,
            lineHeight: 1.55,
            marginTop: 3,
            ...descriptionStyle,
          }}
        >
          {description}
        </div>
      ) : null}
    </a>
  );
}
